/** Upload a residual compensation statement (PDF / CSV / Excel) for an agent or a prospect. */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Upload } from 'lucide-react';
import api from '../../lib/api';
import { Card, btnPrimary, errorText, inputCls } from '../finance/financeUi';

export function UploadCard({ agents, platforms, onCreated }) {
  const [file, setFile] = useState(null);
  const [agent, setAgent] = useState('');
  const [prospect, setProspect] = useState('');
  const [platform, setPlatform] = useState(platforms[0] || 'TSYS');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!file) { toast.error('Choose the statement file'); return; }
    if (!agent && !prospect.trim()) { toast.error("Choose an agent or type the prospect's name"); return; }
    const fd = new FormData();
    fd.append('file', file); fd.append('platform', platform); fd.append('agent_user_id', agent); fd.append('prospect_name', prospect);
    setBusy(true);
    try { const a = await api.request('/admin/residual-analyses', { method: 'POST', body: fd }); toast.success('Uploaded — reading the statement'); setFile(null); setProspect(''); onCreated(a); } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <Card title="Upload a residual statement" testid="ra-upload-card">
      <div className="grid gap-3 px-5 py-4 md:grid-cols-2 lg:grid-cols-4">
        <label className="flex h-10 cursor-pointer items-center gap-2 truncate rounded-md border border-dashed bg-background px-3 text-sm hover:bg-muted" data-testid="ra-file-label">
          <Upload className="h-4 w-4 shrink-0" /><span className="truncate">{file ? file.name : 'PDF, CSV or Excel'}</span>
          <input type="file" accept=".pdf,.csv,.xlsx" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="ra-file-input" />
        </label>
        <select className={inputCls} value={agent} onChange={(e) => setAgent(e.target.value)} data-testid="ra-agent-select">
          <option value="">Prospect (no account yet)</option>
          {agents.map((a) => <option key={a.user_id} value={a.user_id}>{a.name}{a.name !== a.email ? ` · ${a.email}` : ''}</option>)}
        </select>
        {agent ? <div className="hidden lg:block" /> : <input className={inputCls} placeholder="Prospect name" value={prospect} onChange={(e) => setProspect(e.target.value)} data-testid="ra-prospect-input" />}
        <select className={inputCls} value={platform} onChange={(e) => setPlatform(e.target.value)} data-testid="ra-platform-select">
          {platforms.map((p) => <option key={p} value={p}>Price on {p}</option>)}
        </select>
      </div>
      <div className="flex flex-col gap-3 border-t px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground">Spreadsheets are read column by column; PDFs are read by AI. You review every number before the agent sees it.</p>
        <button type="button" className={`${btnPrimary} shrink-0 whitespace-nowrap`} disabled={busy} onClick={submit} data-testid="ra-upload-btn">{busy ? 'Uploading…' : 'Upload & analyze'}</button>
      </div>
    </Card>
  );
}
